import { css } from "@emotion/react"
import { Button, Loading, Typography } from "matsuri-ui"
import { useMemo, useState } from "react"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import dayjs from "dayjs"
import { Capacitor } from "@capacitor/core"
import { useTTLockKeyUnlock } from "@/hooks/useTTLockKeyUnlock"
import { useTranslation } from "@/hooks/useTranslation"

const RoomKeyNumber = ({
    ttlockKeyNumber,
    onClose
}: {
    ttlockKeyNumber: string
    onClose: () => void
}) => {
    const { t } = useTranslation()
    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                gap: 20px;
                align-items: center;
            `}
        >
            <div>
                <Typography
                    bolder
                    variant="h1"
                    css={css`
                        color: "#2E3A57";
                    `}
                >
                    TTLock
                </Typography>
            </div>
            <div>
                <Typography
                    bolder
                    variant="body"
                    css={css`
                        color: "#4A4A4A";
                    `}
                >
                    {t("解錠番号")}
                </Typography>
            </div>
            <div
                css={css`
                    display: flex;
                    gap: 12px;
                `}
            >
                {ttlockKeyNumber.split("").map((digit, index) => (
                    <div
                        key={index}
                        css={css`
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 34px;
                            height: 56px;
                            font-size: 20px;
                            font-weight: 700;
                            background-color: #e6f0f2;
                        `}
                    >
                        {digit}
                    </div>
                ))}
            </div>
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    align-items: center;
                `}
            >
                <Typography
                    bolder
                    variant="body"
                    css={css`
                        color: "#4A4A4A";
                    `}
                >
                    {t("解錠方法")}
                </Typography>
                <Typography
                    css={css`
                        color: "#4A4A4A";
                    `}
                >
                    {t(
                        "TTLockを長押しして番号を表示させたあと、解錠番号を入力し、「#」または右下の解錠マークを押す。"
                    )}
                </Typography>
            </div>
            <Button
                css={css`
                    width: 264px;
                `}
                variant={"filled"}
                color={"primary"}
                onClick={onClose}
            >
                OK
            </Button>
        </div>
    )
}

export const UnlockTTLock = ({
    cleaningId,
    listingId,
    cleaningDateString,
    onClose
}: {
    cleaningId?: string
    listingId?: string
    cleaningDateString?: string
    onClose: () => void
}) => {
    const { t } = useTranslation()
    const { unlock, progressUnlocking, unlockResult, keyNumber } =
        useTTLockKeyUnlock({ cleaningId, listingId })

    // 今がcleaningDateStringの日付なら解錠できる
    const canUnlock = useMemo(() => {
        if (!cleaningDateString) {
            return true
        }
        const cleaningDate = dayjs(cleaningDateString)
        const now = dayjs()
        return now.isSame(cleaningDate, "day")
    }, [cleaningDateString])

    const [showKeyNumber, setShowKeyNumber] = useState(
        !Capacitor.isNativePlatform()
        // 正式リリース前にコメントアウト
        // Capacitor.getPlatform() !== "ios" && !isIOSBrowser()
    )

    if (showKeyNumber) {
        return <RoomKeyNumber ttlockKeyNumber={keyNumber} onClose={onClose} />
    }

    if (progressUnlocking) {
        return (
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 200px;
                `}
            >
                <Typography>{t("解錠中")}</Typography>
                <Typography variant="h1">
                    <Loading />
                </Typography>
            </div>
        )
    }

    if (unlockResult?.success) {
        setTimeout(() => {
            onClose()
        }, 2000)
        return (
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 200px;
                `}
            >
                <Typography>{t("解錠しました！")}</Typography>
                <CheckCircleOutlineIcon
                    css={css`
                        font-size: 48px;
                    `}
                />
            </div>
        )
    }

    if (unlockResult?.formattedErrorMessage) {
        return (
            <div
                css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                `}
            >
                <Typography
                    bolder
                    variant="h1"
                    css={css`
                        color: "#E24242";
                    `}
                >
                    {t("エラー")}
                </Typography>
                <div
                    css={css`
                        width: 264px;
                        margin-top: 16px;
                        white-space: pre-wrap;
                    `}
                >
                    <div>{unlockResult.formattedErrorMessage}</div>
                    <button onClick={() => setShowKeyNumber(true)}>
                        <u>{t("パスコードを確認する")}</u>
                    </button>
                </div>
                <Button
                    css={css`
                        width: 264px;
                        margin-top: 16px;
                    `}
                    variant={"filled"}
                    color={"primary"}
                    onClick={onClose}
                >
                    OK
                </Button>
            </div>
        )
    }

    // 正式リリース前にコメントアウト
    // if (isIOSBrowser()) {
    //     return (
    //         <div
    //             css={css`
    //                 display: flex;
    //                 flex-direction: column;
    //                 align-items: center;
    //             `}
    //         >
    //             <div>
    //                 <Typography
    //                     bolder
    //                     variant="h1"
    //                     css={css`
    //                         color: "#2E3A57";
    //                     `}
    //                 >
    //                     TTLock
    //                 </Typography>
    //             </div>
    //             <div
    //                 css={css`
    //                     width: 264px;
    //                     margin-top: 16px;
    //                 `}
    //             >
    //                 アプリでのみ解錠操作が可能です。
    //                 <button onClick={() => setShowKeyNumber(true)}>
    //                     <u>パスコードを確認する</u>
    //                 </button>
    //             </div>
    //             <Button
    //                 css={css`
    //                     width: 264px;
    //                     margin-top: 16px;
    //                 `}
    //                 variant={"filled"}
    //                 color={"primary"}
    //                 onClick={() => {
    //                     window.open(
    //                         "https://apps.apple.com/us/app/m2m-cleaning/id6743433333",
    //                         "_blank"
    //                     )
    //                 }}
    //             >
    //                 アプリで開く <LaunchIcon />
    //             </Button>
    //             <Button
    //                 css={css`
    //                     width: 264px;
    //                     margin-top: 8px;
    //                 `}
    //                 variant={"filled"}
    //                 onClick={onClose}
    //             >
    //                 キャンセル
    //             </Button>
    //         </div>
    //     )
    // }

    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
            `}
        >
            <div>
                <Typography
                    bolder
                    variant="h1"
                    css={css`
                        color: "#2E3A57";
                    `}
                >
                    TTLock
                </Typography>
            </div>
            <div
                css={css`
                    width: 264px;
                    margin-top: 16px;
                `}
            >
                {canUnlock ? (
                    <>
                        <div>
                            {t("TTLockを開錠しますか？")}
                            <br />
                            {t("※端末のBluetoothをオンにしてください。")}
                        </div>
                        <button onClick={() => setShowKeyNumber(true)}>
                            <u>{t("パスコードを確認する")}</u>
                        </button>
                    </>
                ) : (
                    <>{t("清掃日のみ、解錠操作が可能です。")}</>
                )}
            </div>
            <Button
                css={css`
                    width: 264px;
                    margin-top: 16px;
                `}
                variant={"filled"}
                color={"primary"}
                onClick={unlock}
                disabled={!canUnlock}
            >
                {t("解錠する")}
            </Button>
            <Button
                css={css`
                    width: 264px;
                    margin-top: 8px;
                `}
                variant={"filled"}
                onClick={onClose}
            >
                {t("キャンセル")}
            </Button>
        </div>
    )
}
